import { Box, Flex, Spinner, Text, VStack } from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useMemo } from "react";
import { MdChevronRight } from "react-icons/md";
import { Link } from "react-router-dom";
import { studentPalette } from "../../constants/studentPalette";
import { studentSurfaces } from "../../constants/studentThemeSurfaces";
import { useStartsInLabel } from "../../hooks/useStartsInLabel";
import {
  buildRecentItems,
  RECENT_CONTENT_TYPE_META,
} from "../../utils/studentRecentContent";

const C = {
  primary: studentPalette.primary,
  secondary: studentPalette.secondary,
  cream: studentPalette.cream,
  border: studentSurfaces.border,
  paper: studentSurfaces.raised,
  muted: studentSurfaces.textMuted,
};

const thinScrollCss = {
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(91, 13, 22, 0.35) transparent",
  "&::-webkit-scrollbar": { width: "4px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(91, 13, 22, 0.35)",
    borderRadius: "999px",
  },
  "&::-webkit-scrollbar-button": { display: "none", height: 0, width: 0 },
};

function RecentItemRow({ item, isLast }) {
  const meta = RECENT_CONTENT_TYPE_META[item.type] || RECENT_CONTENT_TYPE_META.video;
  const Icon = meta.Icon;
  const dateLabel = item.date ? dayjs(item.date).format("D MMM") : "—";
  const startsIn = useStartsInLabel(item.startTime);

  return (
    <Flex
      as={Link}
      to={item.to}
      align="center"
      gap={3}
      py={3.5}
      px={2}
      borderBottomWidth={isLast ? 0 : "1px"}
      borderColor={C.border}
      _hover={{
        bg: "rgba(91, 13, 22, 0.04)",
        textDecoration: "none",
      }}
      transition="background 0.15s"
      flexShrink={0}
      mx={-1}
      borderRadius="md"
    >
      <Flex
        w="40px"
        h="40px"
        bg={meta.accent}
        color={C.cream}
        borderRadius="md"
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Icon size={18} />
      </Flex>
      <Box minW={0} flex="1">
        <Flex align="center" gap={2} mb={0.5} flexWrap="wrap">
          <Text
            fontSize="9px"
            fontWeight="800"
            color={C.muted}
            textTransform="uppercase"
            letterSpacing="0.1em"
          >
            {meta.label}
          </Text>
          <Text fontSize="9px" fontWeight="700" color={`${C.muted}cc`}>
            · {dateLabel}
          </Text>
        </Flex>
        <Text
          fontSize="sm"
          fontWeight="700"
          color={C.secondary}
          noOfLines={1}
          sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
        >
          {item.title}
        </Text>
        {startsIn ? (
          <Text
            fontSize="xs"
            fontWeight="800"
            color={C.primary}
            noOfLines={1}
            mt={0.5}
            fontVariantNumeric="tabular-nums"
            sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
          >
            {startsIn}
          </Text>
        ) : (
          <Text fontSize="xs" color={C.muted} noOfLines={1} mt={0.5}>
            {item.subtitle}
          </Text>
        )}
      </Box>
      <Box
        as="span"
        display="inline-flex"
        color={C.primary}
        flexShrink={0}
        opacity={0.7}
        sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
      >
        <MdChevronRight size={20} />
      </Box>
    </Flex>
  );
}

const RecentContentPanel = ({ performanceData, isLoading }) => {
  const recentItems = useMemo(() => buildRecentItems(performanceData), [performanceData]);

  return (
    <Box
      p={{ base: 5, md: 6 }}
      bg={C.paper}
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="lg"
      h="full"
      maxH={{ base: "360px", lg: "420px" }}
      display="flex"
      flexDirection="column"
      minW={0}
      boxShadow="sm"
    >
      <Box flexShrink={0} mb={3}>
        <Text
          fontSize="10px"
          fontWeight="800"
          letterSpacing="0.2em"
          color={`${C.secondary}99`}
          textTransform="uppercase"
          mb={1}
          sx={{ "html[data-theme='dark'] &": { color: "var(--text-muted)" } }}
        >
          Recent content
        </Text>
        <Text
          fontSize="sm"
          fontWeight="800"
          color={C.primary}
          sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
        >
          Latest from your courses
        </Text>
      </Box>

      {isLoading && recentItems.length === 0 ? (
        <Flex flex="1" align="center" justify="center" py={8}>
          <Spinner size="md" color={C.primary} thickness="3px" />
        </Flex>
      ) : recentItems.length === 0 ? (
        <Text fontSize="sm" color={C.muted} lineHeight="tall">
          No recent videos, exams, PDFs, or homework yet.
        </Text>
      ) : (
        <Box
          className="scroll"
          flex="1"
          minH={0}
          overflowY="auto"
          overflowX="hidden"
          pr={1}
          css={thinScrollCss}
        >
          <VStack align="stretch" spacing={0}>
            {recentItems.map((item, index) => (
              <RecentItemRow
                key={item.id}
                item={item}
                isLast={index === recentItems.length - 1}
              />
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default RecentContentPanel;
